/**
 * 都市商务模板 - 单页
 *
 * 设计核心理念：深色左对齐标题栏 + 宽版阅读区
 * 与 sunset（琥珀渐变居中头部 + 装饰线）完全不同的排版骨架：
 * - 顶部：深藏青渐变左对齐标题条
 * - 内容：max-w-4xl 宽版阅读区 + 白卡
 * - 链接色：藏青 + 橙色 hover
 */

import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/templates/urban/components/Navbar";
import UrbanFooter from "@/templates/urban/components/Footer";
import { getSinglePage } from "@/utils/api";

export default function UrbanSinglePage() {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    getSinglePage(slug)
      .then((data) => setPage(data))
      .catch(() => null)
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <LoadingSkeleton />;
  if (!page) return <NotFound />;

  return (
    <div className="min-h-screen bg-[#F4F6F8]">
      <Navbar />

      {/* ── 深色标题条 ── */}
      <section className="relative bg-gradient-to-br from-[#16283F] via-[#1B3A5C] to-[#2C5A8C] py-14 overflow-hidden">
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#F08A24]/10 blur-3xl" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-8">
          {/* 面包屑 */}
          <nav className="flex items-center gap-1.5 text-xs text-gray-400 mb-4">
            <Link to="/" className="hover:text-[#F5A94D] transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-gray-200">{page.title}</span>
          </nav>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-white leading-tight">
            {page.title}
          </h1>
          <div className="w-12 h-1 bg-gradient-to-r from-[#F08A24] to-[#F5A94D] rounded-full mt-4" />
        </div>
      </section>

      {/* ── 内容区 ── */}
      <article className="py-10 sm:py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-8">
          <div className="bg-white rounded-xl border border-gray-100 p-6 sm:p-10 shadow-sm">
            {page.content ? (
              <div
                className="prose prose-lg max-w-none prose-headings:text-gray-800 prose-p:text-gray-600 prose-p:leading-loose prose-a:text-[#1B3A5C] prose-a:no-underline hover:prose-a:text-[#E26A1B] prose-strong:text-gray-800 prose-img:rounded-xl"
                dangerouslySetInnerHTML={{ __html: page.content }}
              />
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-xl bg-[#1B3A5C]/5">
                  <i className="ri-file-text-line text-2xl text-[#1B3A5C]/40" />
                </div>
                <p className="text-gray-400">暂无内容</p>
              </div>
            )}
          </div>

          {/* 返回首页按钮 */}
          <div className="mt-8 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-[#E26A1B] transition-colors group"
            >
              <i className="ri-arrow-left-line group-hover:-translate-x-1 transition-transform" />
              返回首页
            </Link>
          </div>
        </div>
      </article>

      <UrbanFooter />
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="min-h-screen bg-[#F4F6F8]">
      <Navbar />
      <section className="bg-gradient-to-br from-[#16283F] via-[#1B3A5C] to-[#2C5A8C] py-14">
        <div className="max-w-4xl mx-auto px-6 animate-pulse">
          <div className="w-40 h-3 bg-white/20 rounded" />
          <div className="w-64 h-8 bg-white/25 rounded-lg mt-4" />
          <div className="w-12 h-1 bg-white/20 rounded-full mt-4" />
        </div>
      </section>
      <div className="py-12 max-w-4xl mx-auto px-6">
        <div className="animate-pulse space-y-4 p-10 bg-white rounded-xl border border-gray-100">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className={`bg-gray-200 rounded ${i === 0 ? "h-7 w-2/3" : i === 5 ? "h-4 w-1/3" : "h-4 w-full"}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function NotFound() {
  return (
    <div className="min-h-screen bg-[#F4F6F8] flex items-center justify-center">
      <div className="text-center px-4">
        <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-2xl bg-[#1B3A5C]/5">
          <i className="ri-file-damage-line text-3xl text-[#1B3A5C]/40" />
        </div>
        <h1 className="text-xl font-bold text-gray-800 mb-2">页面未找到</h1>
        <p className="text-sm text-gray-500 mb-6">您访问的单页不存在或已被删除</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#F08A24] to-[#E26A1B] text-white text-sm font-medium rounded-lg hover:from-[#E26A1B] hover:to-[#C95A12] transition-all shadow-md"
        >
          <i className="ri-home-4-line" />
          返回首页
        </Link>
      </div>
    </div>
  );
}
